import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { User, ChangePasswordRequest } from '../../auth-pages/models/user.model';

interface UpdateProfileRequest {
    fullname: string;
    lastname?: string;
    email: string;
    mail?: string;
    /** Teléfono con prefijo E.164 incluido. Cadena vacía = limpiar. */
    phone?: string;
    /** Cargo en la empresa. Cadena vacía = limpiar. */
    jobTitle?: string;
    /** "Sobre mí" (máx. 160). Cadena vacía = limpiar. */
    bio?: string;
}

export type MailProvider = 'google' | 'microsoft';
export type MailConnectionStatus = 'CONNECTED' | 'REVOKED' | 'ERROR';

export interface MailAccountResponse {
    id: number;
    provider: MailProvider;
    mailAddress: string;
    connectionStatus: MailConnectionStatus;
    tokenExpiresAt?: string | null;
    scopes?: string | null;
    lastConnectedAt?: string | null;
    lastError?: string | null;
    /** @deprecated alias legacy — el back devuelve `connectionStatus` */
    status?: MailConnectionStatus;
}

export interface MailAuthorizeUrlResponse {
    url: string;
}

export interface DisconnectResponse {
    message?: string;
    provider?: string;
    status?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ProfileApiService {
    private readonly apiUrl = `${environment.apiUrl}api/auth/me`;
    private readonly usersApiUrl = `${environment.apiUrl}api/auth/users`;
    private readonly mailOauthApiUrl = `${environment.apiUrl}api/communication/mail/oauth`;

    constructor(private http: HttpClient) { }

    updateProfile(data: UpdateProfileRequest): Observable<User> {
        const payload: Record<string, unknown> = {
            fullname: data.fullname,
            lastname: data.lastname,
            mail: data.email
        };

        // El backend distingue ausente (no tocar) de "" (limpiar), así que solo
        // se envían los campos que el formulario realmente maneja.
        if (data.phone !== undefined) payload['phone'] = data.phone;
        if (data.jobTitle !== undefined) payload['jobTitle'] = data.jobTitle;
        if (data.bio !== undefined) payload['bio'] = data.bio;

        return this.http.put<any>(this.apiUrl, payload).pipe(
            map(response => ({
                ...response,
                email: response.mail || response.email
            }))
        );
    }

    changePassword(data: ChangePasswordRequest): Observable<any> {
        return this.http.put<any>(`${this.apiUrl}/password`, data);
    }

    getProfile(): Observable<User> {
        return this.http.get<any>(this.apiUrl).pipe(
            map(response => ({
                ...response,
                email: response.mail || response.email
            }))
        );
    }

    updateTimezone(data: { timezone: string | null }): Observable<User> {
        return this.http.put<any>(this.apiUrl, data).pipe(
            map(response => ({
                ...response,
                email: response.mail || response.email
            }))
        );
    }

    getMailAccounts(): Observable<MailAccountResponse[]> {
        return this.http.get<MailAccountResponse[]>(`${this.mailOauthApiUrl}/accounts`);
    }

    getAuthorizeUrl(provider: MailProvider): Observable<MailAuthorizeUrlResponse> {
        return this.http.get<MailAuthorizeUrlResponse>(`${this.mailOauthApiUrl}/${provider}/authorize`);
    }

    disconnectMailAccount(provider: MailProvider): Observable<DisconnectResponse> {
        return this.http.post<DisconnectResponse>(`${this.mailOauthApiUrl}/${provider}/disconnect`, {});
    }

    deleteMyData(): Observable<void> {
        return this.http.delete<void>(this.apiUrl + '/data');
    }

    /**
     * Sube la foto de perfil. El backend reemplaza la anterior y devuelve el usuario
     * con `avatarUrl` ya actualizado.
     */
    uploadAvatar(userId: number, file: File): Observable<User> {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.post<any>(`${this.usersApiUrl}/${userId}/avatar`, formData).pipe(
            map(response => ({
                ...response,
                email: response.mail || response.email
            }))
        );
    }

    /** Elimina la foto de perfil (204, sin cuerpo). */
    deleteAvatar(userId: number): Observable<void> {
        return this.http.delete<void>(`${this.usersApiUrl}/${userId}/avatar`);
    }
}